import React, { useState, useEffect } from 'react';
import { X, ExternalLink, MessageSquare, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import ImageUpload from '../common/ImageUpload';

// FormWrapper moved outside to prevent recreation on every render
const FormWrapper = ({ children, inline, handleSubmit }) => {
  if (inline) {
    return (
      <form className="bg-white w-full" onSubmit={handleSubmit}>
        {children}
      </form>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form
        className="bg-white rounded-lg shadow-lg w-full relative border-2 border-[#1086a4] max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-2xl"
        onSubmit={handleSubmit}
      >
        {children}
      </form>
    </div>
  );
};

const SubmissionForm = ({ mode = 'view', submission = null, onClose, onSubmit, inline = false }) => {
  const [formData, setFormData] = useState({
    link: '',
    comment: '',
    achievementImages: [],
    status: 'pending',
    adminComment: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (submission) {
      setFormData({
        link: submission.link || '',
        comment: submission.comment || '',
        achievementImages: submission.achievementImages?.length ? submission.achievementImages : (submission.achievementImage ? [submission.achievementImage] : []),
        status: submission.status || 'pending',
        adminComment: submission.adminComment || ''
      });
    }
  }, [submission]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (submission?.submissionFormId?.achievementImageRequired && (!formData.achievementImages || formData.achievementImages.length === 0)) {
      newErrors.achievementImages = 'At least one image is required';
    }
    
    // Validate admin comment is required for rejections and more info needed
    if ((formData.status === 'rejected' || formData.status === 'more_info_needed') && (!formData.adminComment || !formData.adminComment.trim())) {
      newErrors.adminComment = 'Admin comment is required when rejecting or requesting more information';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      setErrors({ submit: 'Failed to save submission. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'more_info_needed':
        return <AlertCircle className="h-5 w-5 text-amber-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'more_info_needed':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'more_info_needed':
        return 'More Information Needed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <FormWrapper inline={inline} handleSubmit={handleSubmit}>
        {/* Header */}
        <div className="p-6 border-b border-[#1086a4]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#054581]">
              {isView && 'View Submission'}
              {isEdit && 'Edit Submission'}
            </h2>
            {!inline && (
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                disabled={isSubmitting}
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-4 sm:p-6">
          {/* Student Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-3">Student Information</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Student Name</p>
                  <p className="text-sm text-gray-900">{submission?.userId?.name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Email</p>
                  <p className="text-sm text-gray-900">{submission?.userId?.email || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Submission Form</p>
                  <p className="text-sm text-gray-900">{submission?.submissionFormId?.title || 'Unknown Form'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Submitted Date</p>
                  <p className="text-sm text-gray-900">
                    {submission?.createdAt ? new Date(submission.createdAt).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>

            {/* Collaborators */}
            {submission?.collaborators && submission.collaborators.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-600 mb-2">Collaborators</p>
                <div className="flex flex-wrap gap-2">
                  {submission.collaborators.map(collab => (
                    <span
                      key={collab._id || collab}
                      className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      {collab.name ? `${collab.name} ${collab.lastName || ''}`.trim() : collab}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Status Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-3">Status</h3>
            <div className="flex items-center gap-3">
              {getStatusIcon(formData.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(formData.status)}`}>
                {getStatusLabel(formData.status)}
              </span>
            </div>
            {isEdit && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-[#054581] mb-2">Update Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
                  className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="more_info_needed">More Information Needed</option>
                </select>
              </div>
            )}
          </div>

          {/* Link Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-3">Link</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {formData.link ? (
                <div className="flex items-center gap-2">
                  <a
                    href={formData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1086a4] hover:text-[#054581] transition-colors flex items-center gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {formData.link}
                  </a>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No link provided</p>
              )}
            </div>
          </div>

          {/* Title/Topic Section - only shown when data exists */}
          {(submission?.eventTitle !== undefined && submission?.eventTitle !== null && submission?.eventTitle !== '') && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#054581] mb-3">Title/Topic</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-900">{submission.eventTitle}</p>
              </div>
            </div>
          )}

          {/* Date of Event/Post Section - only shown when data exists */}
          {submission?.eventDate && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#054581] mb-3">Date of Event/Post</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-900">{new Date(submission.eventDate).toLocaleDateString()}</p>
              </div>
            </div>
          )}

          {/* Admin Comment Section (for rejections) */}
          {isEdit && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#054581] mb-3">Admin Comment</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <textarea
                  value={formData.adminComment || ''}
                  onChange={(e) => setFormData(prev => ({...prev, adminComment: e.target.value}))}
                  className={`w-full px-3 py-2 bg-white border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.adminComment
                      ? 'border-red-500 focus:border-red-400'
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  rows={4}
                  placeholder={(formData.status === 'rejected' || formData.status === 'more_info_needed') ? "Comment is required — describe what the student needs to do..." : "Optional admin comment..."}
                  disabled={isSubmitting}
                  required={formData.status === 'rejected' || formData.status === 'more_info_needed'}
                />
                {errors.adminComment && (
                  <p className="text-red-500 text-sm mt-1">{errors.adminComment}</p>
                )}
              </div>
            </div>
          )}

          {/* Comment Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-3">Student Comment</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {formData.comment ? (
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{formData.comment}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No comment provided</p>
              )}
            </div>
          </div>

          {/* Achievement Image Section */}
          {(formData.achievementImages?.length > 0 || submission?.submissionFormId?.achievementImageRequired) && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#054581] mb-3">Screenshots</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ImageUpload
                  value={formData.achievementImages}
                  onChange={() => {}}
                  disabled={true}
                  required={false}
                  error={null}
                  multiple={true}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isEdit && (
          <div className="border-t border-[#1086a4] p-4 sm:p-6">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {inline ? 'Back to List' : 'Cancel'}
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
        {/* View mode footer for inline display */}
        {isView && inline && (
          <div className="border-t border-[#1086a4] p-4 sm:p-6">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                Back to List
              </button>
            </div>
          </div>
        )}
    </FormWrapper>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(SubmissionForm); 